"use client";

import { Dialog, Transition } from "@headlessui/react";
import { useSearchParams } from "next/navigation";
import { Fragment } from "react";
import Balancer from "react-wrap-balancer";

import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useScreenerQuota } from "@/features/screener/services/get-screener-quota";
import { PricingCards } from "@/features/stock-mining-pricing/components/cards";
import { DurationSwitch } from "@/features/stock-mining-pricing/components/switch";
import { ChargeDuration } from "@/features/stock-mining-pricing/data/duration";
import {
  TiersData,
  tiersSchema,
} from "@/features/stock-mining-pricing/types/pricing-tiers";
import { getPricingDataQueryKeyMap } from "@/features/stock-mining-pricing/utils/query-keys";
import { getMaxBrowsingUsage } from "@/features/stripe/utils/stripe-plan";
import { useAppSelector } from "@/hooks/redux-hooks";
import { useLang } from "@/hooks/use-lang";
import { usePublicData } from "@/services/public-data";

import { useSubscribeDialogFeature } from "./use-subscribe-dialog-feature";

export const SubscribeDialog = () => {
  const { t, lang } = useLang();
  const { feature } = useSubscribeDialogFeature();
  const { isOpen } = useAppSelector((state) => state.subscribeDialog);
  const { data, isLoading, isError } = usePublicData<TiersData>({
    itemName: getPricingDataQueryKeyMap(lang).pricingTiers,
    schema: tiersSchema,
    enabled: isOpen,
  });
  const searchParams = useSearchParams();
  const duration = (searchParams.get("duration") || "yearly") as ChargeDuration;

  const { data: quotaData } = useScreenerQuota();
  const screenerQuota = quotaData?.available ?? 0;

  const { profile } = useAuth();
  const currentPlan = profile?.plan ?? "Free";
  const maxBrowsingUsage = getMaxBrowsingUsage({ plan: currentPlan });

  const textMap = {
    screener: {
      reachLimit: t.pricing.reachScreenerLimit(screenerQuota),
      unlock: t.pricing.unlockScreenerLimit,
    },
    winwingpt: {
      reachLimit: t.pricing.reachBrowsingLimit(maxBrowsingUsage),
      unlock: t.pricing.unlockBrowsingLimit,
    },
    others: {
      reachLimit: t.pricing.reachBrowsingLimit(maxBrowsingUsage),
      unlock: t.pricing.unlockBrowsingLimit,
    },
  };

  const { reachLimit, unlock } = textMap[feature as keyof typeof textMap];

  if (isLoading || isError) return null;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="absolute z-[99998]" onClose={() => null}>
        <div className="fixed inset-0 z-[99998] flex items-end justify-center scrollbar-hide">
          <Dialog.Panel
            aria-label="subscribe-dialog"
            className="relative h-full w-full transform overflow-auto bg-white p-4 py-9 pt-32 text-left shadow-xl transition-all sm:m-7 sm:max-h-[80%] sm:max-w-6xl sm:rounded-2xl sm:px-12 sm:pt-5"
          >
            <div className="flex flex-col items-center gap-y-7 @container">
              <div className="space-y-2 text-center">
                <h3 className="text-2xl font-bold">
                  <Balancer>{reachLimit}</Balancer>
                </h3>
                <p className="text-sm text-zinc-500">
                  <Balancer>{unlock}</Balancer>
                </p>
              </div>
              <DurationSwitch
                duration={duration}
                durationTexts={{
                  monthly: t.pricing.monthly,
                  yearly: t.pricing.yearly,
                }}
                containerClassName="border-[1px] border-zinc-200"
                buttonClassName=""
              />
              {data ? <PricingCards data={data} duration={duration} /> : null}
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
